import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadOffIcon from '@mui/icons-material/FileDownloadOff';
import SendIcon from '@mui/icons-material/Send';
import { Box } from '@mui/material';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { EnhancedTabs, TabItem } from 'components/enhanced/tabs/EnhancedTabs';
import { ActionMenuButton } from 'components/ui/buttons/ActionMenuButton';
import { TenantPage } from 'layouts';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import toast from 'shared/utils/toast';
import InvoceDDTCard from '../components/card/InvoceDDTCard';
import { InvoiceBaseInfo } from '../components/tabs/InvoiceBaseInfo';
import { IInvoice } from '../types';
import { transportDocumentLineService } from 'features/inventory/transport-documents/TransportDocument/api/transport-document-line.service';
import { invoiceService, equipmentInvoiceService } from 'shared/services';
import { TransportDocumentLinesColumns } from 'features/inventory/transport-documents/TransportDocument/transport-document-lines';

const TRANSLATION_PREFIX = 'pages.invoices.update';

const DOWNLOADABLE_FIELDS = [
  'invoice_file',
  'invoice_revision_file',
  'extra_1_file',
  'extra_2_file',
  'extra_3_file'
];

export function InvoiceDetail() {
  const { t } = useTranslation();
  const { invoiceId } = useParams();
  const dataGridRefreshRef = useRef(null);

  const [invoice, setInvoice] = useState<IInvoice>();
  const [isAlertErrorColorVisible, setIsAlertErrorColorVisible] = useState(false);
  const [isGeneratingCreditMemo, setIsGeneratingCreditMemo] = useState<boolean>(false);

  const refreshInvoice = () => {
    invoiceService.get(+invoiceId).then(setInvoice);
  };

  useEffect(() => {
    refreshInvoice();
  }, []);

  const updateInvoice = (values) => {
    invoiceService.update(+invoiceId, values).then((res) => {
      setInvoice(res);
      toast.crud.successUpdate('Fattura', 'f');
    });
  };

  const downloadInvoice = async () => {
    try {
      await invoiceService.downloadInvoiceAgreement(+invoiceId);
    } catch (err) {
      toast.error('Errore download: Impossibile generare il frontespizio della fattura.');
    }
  };

  const downloadCDFrontspiece = () => {
    invoiceService.downloadCDFrontspieceAgreement(+invoiceId);
  };

  const updateSettings = () => {
    toast.warning(t(`${TRANSLATION_PREFIX}.no-credit-amount`));
    setIsAlertErrorColorVisible(true);
  };

  const sendCreditMemo = () => {
    invoiceService.emailCreditMemo(+invoiceId);
  };

  const handlegenerateCreditMemo = async () => {
    if (!invoice?.credit_memo_file) return;
    setIsGeneratingCreditMemo(true);
    try {
      invoiceService.generateCreditMemo(+invoiceId).then(() => {
        refreshInvoice();
        toast.success(t(`${TRANSLATION_PREFIX}.credit-note-generated`));
      });

      invoiceService.downloadCreditMemo(+invoiceId);
    } catch (err) {
      toast.error(t(`${TRANSLATION_PREFIX}.generation-failed`));
    } finally {
      setIsGeneratingCreditMemo(false);
    }
  };

  const columns = TransportDocumentLinesColumns(false, false);

  const tabConfig: TabItem[] = invoice
    ? [
        {
          label: t(`global.base-info`),
          component: (
            <InvoiceBaseInfo
              invoice={invoice}
              openErrorColor={isAlertErrorColorVisible}
              updateInvoice={updateInvoice}
            />
          )
        },
        {
          label: t(`${TRANSLATION_PREFIX}.invoice-ddts`),
          component: (
            <InvoceDDTCard
              invoice={invoice}
              dataGridRefreshRef={dataGridRefreshRef}
              refreshInvoice={refreshInvoice}
              setIsAlertErrorColorVisible={setIsAlertErrorColorVisible}
            />
          )
        },
        {
          label: t(`${TRANSLATION_PREFIX}.movement-list`),
          component: (
            <EnhancedDataGrid
              columns={columns}
              service={transportDocumentLineService}
              outerParams={{ invoice: invoiceId }}
              refresh={dataGridRefreshRef}
            />
          )
        }
      ]
    : [];

  return (
    <TenantPage
      title={t(`${TRANSLATION_PREFIX}.title`) + ' ' + invoice?.ref}
      subtitle={t(`${TRANSLATION_PREFIX}.subtitle`)}
      entityCode={invoice?.ref}
      menuRight={
        <Box className="flex gap-3">
          <ActionMenuButton
            label={t(`${TRANSLATION_PREFIX}.credit-note`)}
            disabled={isGeneratingCreditMemo}
            items={[
              invoice?.credit_memo_file && {
                label: isGeneratingCreditMemo ? t(`actions.generating`) : t(`actions.download`),
                onClick: handlegenerateCreditMemo,
                loading: isGeneratingCreditMemo,
                icon: <DownloadIcon className="mr-2 text-gray-500" sx={{ fontSize: 18 }} />
              },
              invoice?.credit_memo_file && {
                label: 'Invia',
                onClick: sendCreditMemo,
                icon: <SendIcon className="mr-2 text-gray-500" sx={{ fontSize: 18 }} />
              }
            ]}
          />

          <ActionMenuButton
            label="Scarica"
            variant="outlined"
            color="secondary"
            startIcon={<DownloadIcon />}
            items={[
              { label: t(`${TRANSLATION_PREFIX}.invoice-agreement`), onClick: downloadInvoice },
              invoice?.credit_memo_file &&
                invoice?.amount != null &&
                +invoice.amount !== +invoice.expected_amount &&
                (invoice.credit_memo_ref === ''
                  ? {
                      label: t(`${TRANSLATION_PREFIX}.invoice-credit-debit-agreement`),
                      onClick: updateSettings,
                      icon: (
                        <FileDownloadOffIcon className="mr-2 text-gray-500" sx={{ fontSize: 18 }} />
                      )
                    }
                  : {
                      label: t(`${TRANSLATION_PREFIX}.invoice-credit-debit-agreement`),
                      onClick: downloadCDFrontspiece,
                      icon: <DownloadIcon className="mr-2 text-gray-500" sx={{ fontSize: 18 }} />
                    })
            ]}
          />
        </Box>
      }>
      <EnhancedTabs
        tabs={tabConfig}
        variant="scrollable"
        documentConfig={{
          serviceName: 'invoice',
          entityId: invoice?.id,
          dictParams: { invoice: invoiceId },
          customFolderName: 'menu.invoices/' + invoice?.ref,
          folderDescription: `Data: ${invoice?.date}; `
        }}
        attachmentsConfig={{
          fields: DOWNLOADABLE_FIELDS,
          entity: invoice,
          service: equipmentInvoiceService,
          translationPrefix: 'entities.ddt'
        }}
      />
    </TenantPage>
  );
}

export default InvoiceDetail;
